import { useSessionAudio } from '@/hooks/use-program-card-audio';
import { MediaGuideMetadata } from '@/utils/media/types';
import { useAudio } from '@rhg/hooks/use-audio';
import { useCallback } from 'react';

export interface TrackRowPlaybackState {
    /** This row's guide is the one currently loaded in the audio element. */
    isActiveTrack: boolean;
    /** Active and currently playing (not paused). */
    isPlaying: boolean;
    /** Active but paused. */
    isPaused: boolean;
    /** Pause if playing, resume if paused. */
    toggle: () => void;
}

export function useTrackRowPlayback(
    guide: MediaGuideMetadata
): TrackRowPlaybackState {
    const { isActiveAudio, isPlaying } = useSessionAudio({
        inProgressDTC: true,
        currentSession: guide,
    });
    // useAudio is null when there is no AudioProvider in the tree (e.g. some
    // card surfaces / tests); guard so consumers never crash. Depend on the
    // stable `togglePlay` callback (the memoized context keeps its identity),
    // not the whole context value — that dep used to churn at ~4Hz. EVE-12668.
    const togglePlay = useAudio()?.controls?.togglePlay;

    const toggle = useCallback(() => {
        togglePlay?.();
    }, [togglePlay]);

    return {
        isActiveTrack: isActiveAudio,
        isPlaying: isActiveAudio && isPlaying,
        isPaused: isActiveAudio && !isPlaying,
        toggle,
    };
}
